/** @jsx jsx */
import { Box, Button, CardHorizontalDivider, Flex, Grid, Heading, Paragraph } from "@eworkit/iris";
import { FormikProps } from "formik";
import React, { FC, useContext } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";
import * as Yup from "yup";

import { LazyUserAvatar } from "@/components/avatars";
import { VALIDATION_TYPES } from "@/components/Formik";
import { FormikCheckbox } from "@/components/Formik/FormikCheckbox";
import { ResponsiveCard } from "@/components/ResponsiveCard";
import { UserContext } from "@/components/User";
import { JobApplication } from "@/data/job-applications/types/JobApplication";
import { fetchJobRequestConsent } from "@/data/job-requests";
import { JobRequest } from "@/data/job-requests/types/JobRequest";
import { formatFullName } from "@/data/persons";
import { useFetcher } from "@/hooks/http";
import { useApplyWizard } from "@/views/job-applications/apply/_components/ApplyWizardContext";
import { useApplyWizardDraftUpdate } from "@/views/job-applications/apply/_components/useApplyWizardDraftUpdate";
import { ApplyConsentSection } from "@/views/job-applications/apply/_summary/ApplyConsentSection";

export interface SummaryFormValues {
  consentApproved: boolean;
  completeProfileConsentApproved: boolean;
  updateCvs?: boolean;
  updateAvailability?: boolean;
  updateWorkstyles?: boolean;
  updateContactPersonPhoneNumber?: boolean;
}

export const summaryValidation = Yup.object().shape({
  consentApproved: Yup.boolean().test({ message: VALIDATION_TYPES.CHECKED, test: (value: boolean) => value }),
});

export const summaryCompanyValidation = Yup.object().shape({
  consentApproved: Yup.boolean().test({ message: VALIDATION_TYPES.CHECKED, test: (value: boolean) => value }),
  completeProfileConsentApproved: Yup.boolean().oneOf([true], VALIDATION_TYPES.CHECKED),
});

export const transformDraftWithSummary = (values: SummaryFormValues) => (draft: JobApplication) => ({
  ...draft,
  consentApproved: values.consentApproved,
  updateCvs: values.updateCvs,
  updateAvailability: values.updateAvailability,
  updateWorkstyles: values.updateWorkstyles,
  updateContactPersonPhoneNumber: values.updateContactPersonPhoneNumber,
});

interface ApplySummaryFormProps {
  formik: FormikProps<SummaryFormValues>;
  jobRequest: JobRequest;
  cvsChanged: boolean;
  availabilityChanged: boolean;
  contactPersonPhoneNumberChanged: boolean;
  workStylesChanged: boolean;
}

export const ApplySummaryForm: FC<ApplySummaryFormProps> = ({
  formik,
  jobRequest,
  cvsChanged,
  availabilityChanged,
  contactPersonPhoneNumberChanged,
  workStylesChanged,
}) => {
  const { t } = useTranslation("session");

  const { draft } = useApplyWizard();
  useApplyWizardDraftUpdate(transformDraftWithSummary(formik.values));

  const { contextCompany } = useContext(UserContext);

  const { payload: consent } = useFetcher(fetchJobRequestConsent, {
    jobRequestId: jobRequest.id,
    companyId: contextCompany && contextCompany.id,
  });

  const isContactPerson = draft!.consultantProfile.contactPerson.id === draft!.consultantProfile.parent?.id;

  const showProfileUpdate =
    cvsChanged || availabilityChanged || workStylesChanged || (isContactPerson && contactPersonPhoneNumberChanged);

  const showContactPersonProfileUpdate = !isContactPerson && contactPersonPhoneNumberChanged;

  const profilesToUpdate = (showProfileUpdate ? 1 : 0) + (showContactPersonProfileUpdate ? 1 : 0);

  return (
    <Flex flexDirection="column" gap="md">
      {profilesToUpdate > 0 && (
        <ResponsiveCard>
          <Flex flexDirection="column" gap="md">
            <Box>
              <Heading size="h-sm">
                {profilesToUpdate > 1
                  ? t(`JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.SUMMARY.UPDATE_PROFILES`)
                  : t(`JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.SUMMARY.UPDATE_PROFILE`)}
              </Heading>
              <Flex mt="xxs">
                <Paragraph size="p-md" color="n700">
                  {t(`JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.SUMMARY.UPDATE_PROFILES_INFO`)}
                </Paragraph>
              </Flex>
            </Box>

            <Flex flexDirection="column" flexGrow={1} gap="md">
              {showProfileUpdate && (
                <Grid columns={["1fr", null, null, "1fr 2fr"]} sx={{ alignItems: "flex-start" }}>
                  <Grid>
                    <Flex gap="xs" alignItems="center">
                      <LazyUserAvatar user={draft!.consultantProfile} size="xs" />
                      <Paragraph bold>{formatFullName(draft!.consultantProfile)}</Paragraph>
                    </Flex>
                  </Grid>
                  <Grid>
                    <Grid gap="sm" columns={["1fr", null, null, "1fr 1fr 1fr"]} sx={{ rowGap: "xs" }}>
                      {availabilityChanged && (
                        <Flex flexDirection="row" alignItems="center" gap="xs">
                          <FormikCheckbox name="updateAvailability" checked={formik.values.updateAvailability} />
                          <Paragraph>{t("JOB_APPLICATIONS.DETAIL.SECTION_DETAILS.NOTICE_PERIOD")}</Paragraph>
                        </Flex>
                      )}

                      {cvsChanged && (
                        <Flex flexDirection="row" alignItems="center" gap="xs">
                          <FormikCheckbox name="updateCvs" checked={formik.values.updateCvs} />
                          <Paragraph>{t("JOB_REQUESTS.APPLY.WIZARD.CV")}</Paragraph>
                        </Flex>
                      )}

                      {workStylesChanged && (
                        <Flex flexDirection="row" alignItems="center" gap="xs">
                          <FormikCheckbox name="updateWorkstyles" checked={formik.values.updateWorkstyles} />
                          <Paragraph>{t("JOB_REQUESTS.APPLY.WIZARD.WORKSTYLES")}</Paragraph>
                        </Flex>
                      )}

                      {isContactPerson && contactPersonPhoneNumberChanged && (
                        <Flex flexDirection="row" alignItems="center" gap="xs">
                          <FormikCheckbox
                            name="updateContactPersonPhoneNumber"
                            checked={formik.values.updateContactPersonPhoneNumber}
                          />
                          <Paragraph>
                            {t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.APPLICATION_DETAILS.PHONE_NUMBER")}
                          </Paragraph>
                        </Flex>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              )}

              {showContactPersonProfileUpdate && (
                <React.Fragment>
                  <CardHorizontalDivider sx={{ my: 0 }} size="wide" />

                  <Grid columns={["1fr", null, null, "1fr 2fr"]}>
                    <Grid>
                      <Flex gap="xs" alignItems="center">
                        <LazyUserAvatar user={draft!.consultantProfile.contactPerson} size="xs" />
                        <Paragraph bold>{formatFullName(draft!.consultantProfile.contactPerson)}</Paragraph>
                      </Flex>
                    </Grid>
                    <Grid>
                      <Flex flexDirection="row" alignItems="center" gap="xs">
                        <FormikCheckbox
                          name="updateContactPersonPhoneNumber"
                          checked={formik.values.updateContactPersonPhoneNumber}
                        />
                        <Paragraph>
                          {t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.APPLICATION_DETAILS.PHONE_NUMBER")}
                        </Paragraph>
                      </Flex>
                    </Grid>
                  </Grid>
                </React.Fragment>
              )}
            </Flex>
          </Flex>
        </ResponsiveCard>
      )}

      <ApplyConsentSection formik={formik} consent={consent} jobRequestOrigin={jobRequest.origin.systemOwner} />

      <Box>
        <Button onClick={formik.submitForm} disabled={formik.isSubmitting} size="lg">
          {t(`JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.SUMMARY.APPLY`)}
        </Button>
      </Box>
    </Flex>
  );
};
